import { monthFirstDay, monthLastDay, monthsBetween, wholeMonthsIn } from './months.js';

// THE LEAD WINDOW for a monthly-grain lead source (ClubWise).
//
// ClubWise leads have no date, only the month they were uploaded for. So a lead
// figure is only meaningful over WHOLE months, and only over months that were
// actually UPLOADED. This module decides which months those are. Pure.
//
// ⚠️⚠️ THREE RULES, each a way a figure could otherwise be wrong without looking
// wrong:
//   1. WHOLE MONTHS ONLY. A part-month range contains no month it does not fully
//      cover, so a range starting on the 10th has no lead figure for that month.
//      (Counting the leads dated the 1st of a month whose 1st is outside the range
//      silently drops the whole month.)
//   2. UPLOADED MONTHS ONLY. A month nobody uploaded is "not uploaded", never a
//      zero — a zero says the club took no enquiries.
//   3. EVERY CLUB. Across several clubs, a month counts only if EVERY club that
//      has uploaded anything has uploaded it: club A's September beside a missing
//      club B September would understate the group, with nothing to say so.
//      (A club that has uploaded NOTHING is uncounted on the report already and
//      must not veto every month for everyone else.)

export interface LeadWindow {
  /** Whole months in the selected range, plus any uploaded month-to-date month the range starts on the 1st of. */
  candidateMonths: string[];
  /** Candidate months uploaded for every uploading club. The lead window. */
  measuredMonths: string[];
  /** Candidate months at least one uploading club has not uploaded. */
  notUploadedMonths: string[];
  /** Months (any time) uploaded for every uploading club. Drives the series. */
  uploadedForAll: string[];
  /**
   * ⚠️ MONTH-TO-DATE MONTHS in `measuredMonths`: the export only runs to `dataTo`
   * (the EARLIEST cutoff across the uploading clubs), not to the month's last day.
   * Leads have no day, so a lead figure for such a month is leads TO that date and
   * every lead comparison is withheld. Spend and sales for it stop at `dataTo`.
   */
  monthToDate: Array<{ month: string; dataTo: string }>;
  /** Every uploaded-for-all month that is part-way through, in view or not (labels the 12-month chart). */
  partialUploaded: Array<{ month: string; dataTo: string }>;
  /**
   * ⚠️ Uploaded month-to-date months the range touches but does not START on the
   * 1st of. Their leads cannot be placed inside a range that begins part-way
   * through the month, so they are left out, and the note says why.
   */
  skippedPartial: string[];
}

/** club code → month → the last day the upload covers (null/absent = the whole month). */
export type UploadCutoffs = ReadonlyMap<string, ReadonlyMap<string, string | null>>;

/**
 * ⚠️ HOW CURRENT THE UPLOADED DATA IS: for each club, the cutoff of its LATEST
 * uploaded month (null cutoff = that month's last day); across clubs the EARLIEST,
 * so the figure never overstates freshness. Null when nobody has uploaded.
 */
export function latestDataTo(
  uploadedByClub: ReadonlyMap<string, ReadonlySet<string>>,
  cutoffs?: UploadCutoffs,
): string | null {
  const per: string[] = [];
  for (const [club, months] of uploadedByClub) {
    const latest = [...months].sort().pop();
    if (latest) per.push(cutoffOf(club, latest, cutoffs));
  }
  return per.sort()[0] ?? null;
}

/** A cutoff outside its own month is nonsense: treat it as the whole month. */
function cutoffOf(club: string, month: string, cutoffs: UploadCutoffs | undefined): string {
  const last = monthLastDay(month);
  const raw = cutoffs?.get(club)?.get(month) ?? null;
  if (!raw || raw.slice(0, 7) !== month || raw > last) return last;
  return raw;
}

export function resolveLeadWindow(
  range: { from: string; to: string },
  uploadedByClub: ReadonlyMap<string, ReadonlySet<string>>,
  cutoffs?: UploadCutoffs,
): LeadWindow {
  const uploadingEntries = [...uploadedByClub.entries()].filter(([, s]) => s.size > 0);
  const uploading = uploadingEntries.map(([, s]) => s);
  const whole = wholeMonthsIn(range.from, range.to);
  if (uploading.length === 0) {
    return {
      candidateMonths: whole,
      measuredMonths: [],
      notUploadedMonths: whole,
      uploadedForAll: [],
      monthToDate: [],
      partialUploaded: [],
      skippedPartial: [],
    };
  }
  const inAll = (m: string): boolean => uploading.every((s) => s.has(m));
  const everyMonth = new Set<string>();
  for (const s of uploading) for (const m of s) everyMonth.add(m);
  const uploadedForAll = [...everyMonth].filter(inAll).sort();

  const latestOf = new Map(uploadingEntries.map(([c, s]) => [c, [...s].sort().pop() ?? '']));
  const minCutoff = (m: string): string =>
    uploadingEntries.map(([c]) => cutoffOf(c, m, cutoffs)).sort()[0] ?? monthLastDay(m);
  const maxCutoff = (m: string): string =>
    uploadingEntries.map(([c]) => cutoffOf(c, m, cutoffs)).sort().pop() ?? monthLastDay(m);
  const isPartial = (m: string): boolean => minCutoff(m) < monthLastDay(m);
  // ⚠️ The leniency (counted although the range ends before the cutoff) applies only
  // where every club's short upload is its LATEST month: the in-progress one.
  const lenient = (m: string): boolean =>
    uploadingEntries.every(([c]) => cutoffOf(c, m, cutoffs) >= monthLastDay(m) || latestOf.get(c) === m);

  // Months the range could count: whole months, and uploaded in-progress months it starts on the 1st of.
  const included = (m: string): boolean => {
    if (range.from > monthFirstDay(m) || range.to < monthFirstDay(m)) return false;
    if (range.to >= maxCutoff(m)) return true;
    return isPartial(m) && lenient(m);
  };
  const extra = uploadedForAll.filter((m) => !whole.includes(m) && isPartial(m) && included(m));
  const candidateMonths = [...whole, ...extra].sort();

  const measuredMonths = candidateMonths.filter((m) => inAll(m) && (whole.includes(m) ? true : included(m)));
  const skippedPartial = uploadedForAll.filter(
    (m) =>
      isPartial(m) &&
      !candidateMonths.includes(m) &&
      range.to >= monthFirstDay(m) &&
      range.from <= monthLastDay(m) &&
      range.from > monthFirstDay(m),
  );
  return {
    candidateMonths,
    measuredMonths,
    notUploadedMonths: candidateMonths.filter((m) => !inAll(m)),
    uploadedForAll,
    monthToDate: measuredMonths.filter(isPartial).map((month) => ({ month, dataTo: minCutoff(month) })),
    partialUploaded: uploadedForAll.filter(isPartial).map((month) => ({ month, dataTo: minCutoff(month) })),
    skippedPartial,
  };
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2026-09" → "September 2026". */
export function monthName(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) return key;
  return `${MONTH_NAMES[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

function joinNames(keys: readonly string[]): string {
  const names = keys.map(monthName);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "2026-10-20" → "20 Oct". */
export function shortDay(day: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const mon = MONTH_NAMES[Number(m[1]) - 1]?.slice(0, 3) ?? m[1];
  return `${Number(m[2])} ${mon}`;
}

/** "Leads for October are to 20 Oct (latest upload)." — or null when nothing is month-to-date. */
export function monthToDateLine(w: LeadWindow): string | null {
  if (w.monthToDate.length === 0) return null;
  const parts = w.monthToDate.map((x) => `${monthName(x.month).split(' ')[0]} are to ${shortDay(x.dataTo)}`);
  const body = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `Leads for ${body} (latest upload).`;
}

/**
 * The sentence printed ABOVE the lead figures. Says what the lead numbers are,
 * which months they cover, and what was left out and why.
 */
export function leadBasisNote(w: LeadWindow): string {
  const skipped =
    w.skippedPartial.length === 0
      ? ''
      : ` Leads for ${joinNames(w.skippedPartial)} are only shown when the period starts on the 1st of the month.`;
  if (w.candidateMonths.length === 0) {
    return (
      'Lead figures come from monthly CRM uploads and are shown for whole calendar months, plus the current month to date. ' +
      `This period does not contain a month that can be shown, so leads and conversion rate are not shown. Sales are counted by day.${skipped}`
    );
  }
  if (w.measuredMonths.length === 0) {
    return (
      `Lead figures come from monthly CRM uploads. None has been uploaded for ${joinNames(w.candidateMonths)} yet, ` +
      `so leads and conversion rate are not shown. Sales are counted by day.${skipped}`
    );
  }
  const base =
    `Lead figures come from monthly CRM uploads and cover whole months, plus the current month to date: ${joinNames(w.measuredMonths)}. ` +
    'Conversion rate uses sales from those same months.';
  const mtd = w.monthToDate.length > 0 ? ` ${monthToDateLine(w)} Lead comparisons are not shown while a month is part-way through.` : '';
  const left =
    w.notUploadedMonths.length === 0
      ? ''
      : ` Not yet uploaded for every club, so left out: ${joinNames(w.notUploadedMonths)}.`;
  return `${base}${mtd}${left}${skipped}`;
}

/**
 * ⚠️ THE PREVIOUS EQUIVALENT MONTHS FOR A WINDOW — null whenever any month in it is
 * part-way through: leads have no day, so a month-to-date lead figure compared
 * against a whole month would be a comparison with an understatement.
 */
export function previousMonthsForWindow(
  w: LeadWindow,
  mode: 'previous' | 'lastMonth' | 'lastYear',
): string[] | null {
  if (w.monthToDate.length > 0) return null;
  return previousEquivalentMonths(w.measuredMonths, mode, w.candidateMonths.length, w.uploadedForAll);
}

/** `YYYY-MM` shifted by a whole number of months (negative = earlier). UTC arithmetic. */
export function shiftMonthKey(key: string, delta: number): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) throw new Error(`Invalid month key: ${key}`);
  const idx = Number(m[1]) * 12 + (Number(m[2]) - 1) + delta;
  const y = Math.floor(idx / 12);
  return `${y}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

/**
 * ⚠️⚠️ THE PREVIOUS EQUIVALENT WHOLE MONTHS, for a lead delta. Lead figures exist
 * only for whole months, so a comparison is whole months against whole months:
 *   · lastYear  → each measured month, twelve earlier;
 *   · lastMonth → each, one earlier;
 *   · previous  → the same number of months immediately before the selected
 *                 months (a quarter against the quarter before it).
 * Returns null unless EVERY one of those months was uploaded by every uploading
 * club: a delta against a part-measured window compares a figure with an
 * understatement of itself, and the reader cannot see it.
 */
export function previousEquivalentMonths(
  measured: readonly string[],
  mode: 'previous' | 'lastMonth' | 'lastYear',
  candidateCount: number,
  uploadedForAll: readonly string[],
): string[] | null {
  if (measured.length === 0) return null;
  const sorted = [...measured].sort();
  let shifted: string[];
  if (mode === 'previous') {
    // The window immediately before the selected one has `candidateCount` months;
    // map each measured month into it by its distance from the window's start.
    const n = Math.max(candidateCount, sorted.length);
    shifted = sorted.map((m) => shiftMonthKey(m, -n));
  } else {
    shifted = sorted.map((m) => shiftMonthKey(m, mode === 'lastYear' ? -12 : -1));
  }
  const have = new Set(uploadedForAll);
  return shifted.every((m) => have.has(m)) ? shifted : null;
}

export { monthsBetween };
